import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash/fp/isEqual';

import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { TableState } from 'dashboard/lib/interfaces/TableState';
import { TableViewsState } from 'dashboard/lib/interfaces/TableViewsState';
import { WebhookEventsTableView } from 'dashboard/models/table-view';
import { WebhookEvent, WebhookEventStatus } from 'dashboard/models/webhook-event';
import { PaginationParams } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

export interface WebhookEventsFilterParams {
  eventId?: string;
  webhookId?: string;
  status?: WebhookEventStatus[];
  updatedAtStart?: Iso8601DateString;
  updatedAtEnd?: Iso8601DateString;
  omni?: string;
}

export interface WebhookEventsState
  extends Omit<TableState<WebhookEventsFilterParams>, 'sortParams'>,
    TableViewsState<WebhookEventsTableView> {
  webhookEvents: WebhookEvent[];
}

const initialState: WebhookEventsState = {
  webhookEvents: [],
  paginationParams: undefined,
  filters: {
    eventId: undefined,
    webhookId: undefined,
    status: undefined,
    updatedAtStart: undefined,
    updatedAtEnd: undefined,
    omni: undefined,
  },
  pageSize: DEFAULT_PAGE_SIZE,
  currentPage: 1,
  tableViews: {
    views: [],
    currentTableView: '',
  },
};

const { reducer, actions } = createSlice({
  name: 'webhook-events',
  initialState,
  reducers: {
    setWebhookEvents: (
      state,
      action: PayloadAction<{ webhookEvents: WebhookEvent[]; paginationParams?: PaginationParams }>,
    ) => {
      state.webhookEvents = action.payload.webhookEvents;
      state.paginationParams = action.payload.paginationParams;
    },
    setFilters: (state, action: PayloadAction<WebhookEventsFilterParams>) => {
      const newFilters = {
        ...state.filters,
        ...action.payload,
      };

      if (!isEqual(newFilters, state.filters)) {
        state.filters = newFilters;
      }
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setTableViews: (state, action: PayloadAction<WebhookEventsTableView[]>) => {
      state.tableViews.views = action.payload;
    },
    setCurrentTableView: (state, action: PayloadAction<string>) => {
      state.tableViews.currentTableView = action.payload;
    },
  },
});

export const {
  setWebhookEvents,
  setFilters,
  setPageSize,
  setCurrentPage,
  setTableViews,
  setCurrentTableView,
} = actions;
export const webhookEventsReducer = reducer;
